import React, { useEffect, useRef, useState } from "react";
import styles from "./History.module.css";
import { useDispatch, useSelector } from "react-redux";
import { GetHistoryData } from "../../../../redux/slices/projectSlice";
import { useLocation } from "react-router-dom";

import ComparisonGraph from "./components/ComparisonGraph";

import restructureWorker from "Utils/Historicalhistory.worker";
import comparisonWorker from "Utils/HistoricalComparison.worker";
import WorkerBuilder from "Utils/builder.worker";

import GraphLabelsModel from "../View Data/components/GraphLabelsModel";
import TagSelectionModel from "./components/TagSelectionModel";
import FunctionModel from "./components/FunctionModel";
import ModelPDFDetails from "../View Data/components/ModelPDFDetails";

import { fetchTagGroups } from "../../../../redux/slices/tagGroupSlice";
import GraphInterval from "./components/GraphInterval";
import LineHistory from "./components/LineHistory";
import BarHistory from "./components/BarHistory";
import HistogramHistory from "./components/HistogramHistory";
import DoughnutHistory from "./components/DoughnutHistory";

const History = ({ setPageTitle, setPage }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const uniqueProjectId = searchParams.get("graphId");

  /** ---------------- WORKERS ---------------- */
  const restructure = new WorkerBuilder(restructureWorker);
  const comparisonRestructure = new WorkerBuilder(comparisonWorker);

  /** ---------------- UI STATE ---------------- */
  const [tab, setTab] = useState("Graph");
  const graphRef = useRef(null);

  /** ---------------- DATE FILTER ---------------- */
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  /** ---------------- SINGLE GRAPH ---------------- */
  const [dataArrDay, setDataArrDay] = useState([]);
  const [dateTimesOneDay, setDateTimesOneDay] = useState([]);
  const [chartDataDay, setChartDataDay] = useState({ labels: [], datasets: [] });

  /** ---------------- COMPARISON GRAPH ---------------- */
  const [currentChart, setCurrentChart] = useState(null);
  const [previousChart, setPreviousChart] = useState(null);

  /** ---------------- TABLE ---------------- */
  const [oneDayDataTs, setOneDayDataTS] = useState([]);
  const [channelNames, setChannelNames] = useState([]);

  /** ---------------- SELECTION / MODELS ---------------- */
  const [isGraphLabels, setIsGraphLabels] = useState(false);
  const [isTagGroupsModel, setIsTagGroupsModel] = useState(false);
  const [isFunctionModel, setIsFunctionModel] = useState(false);
  const [isPDFModel, setIsPDFModel] = useState(false);
  const [isXInterval, setIsXInterval] = useState(false);

  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [xInterval, setXInterval] = useState("");
  const [graphType, setGraphType] = useState("Line");
  /** ---------------- DATA CACHE ---------------- */
  const [hisData, setHisData] = useState([]);
  const [comparisonHisData, setComparisonHisData] = useState([]);
  const [allChannels, setAllChannels] = useState([]);

  const [isExporting, setIsExporting] = useState(false);





  const tagGroupsArr = useSelector(
    (state) => state.tagGroup.tagGroupsArr
  );

  /** ---------------- INIT ---------------- */
  useEffect(() => {

    setPageTitle("dashboard");
    setPage("dashboard");
    dispatch(fetchTagGroups(uniqueProjectId));
  }, [uniqueProjectId]);

  /** ---------------- BUILD SINGLE GRAPH ---------------- */
  useEffect(() => {
    const copied = [...dataArrDay];
    copied.forEach((d) => {
      d.pointRadius = 0;
      d.borderWidth = 1.5;
    });

    setChartDataDay({
      labels: dateTimesOneDay,
      datasets: copied,
    });
  }, [dateTimesOneDay]);

  const removeDuplicate = (arr) => [...new Set(arr)];

  /** ---------------- ARRANGE SINGLE GRAPH (UNCHANGED) ---------------- */
  const arrangeData = async (allData) => {
    let channels = [];

    selectedGroups.forEach((group) => {
      const foundGroup = tagGroupsArr.find(
        (info) => info.groupName === group
      );
      if (foundGroup?.channels) {
        channels.push(...foundGroup.channels);
      }
    });

    channels.push(...selectedChannels);
    channels = removeDuplicate(channels);

    restructure.postMessage({ data: allData, channels });

    restructure.onmessage = (e) => {
      setDataArrDay(e.data.chartArray);
      setDateTimesOneDay(e.data.timeArray);

      setOneDayDataTS(e.data.tableArray);

      setChannelNames(e.data.headers);

      if (!allChannels.length) {
        setAllChannels(e.data.headers);
      }
    };
  };

  /** ---------------- ARRANGE COMPARISON GRAPH ---------------- */
  const arrangeComparisonData = (apiData) => {
    if (!apiData?.currentRange || !apiData?.previousRange) return;

    let channels = [];

    selectedGroups.forEach((group) => {
      const foundGroup = tagGroupsArr.find(
        (info) => info.groupName === group
      );
      if (foundGroup?.channels) {
        channels.push(...foundGroup.channels);
      }
    });

    channels.push(...selectedChannels);
    channels = removeDuplicate(channels);

    comparisonRestructure.postMessage({
      currentData: apiData.currentRange.data,
      previousData: apiData.previousRange.data,
      channels,
    });

    comparisonRestructure.onmessage = (e) => {

      setCurrentChart(e.data.current);
      setPreviousChart(e.data.previous);
    };
  };

  /** ---------------- SUBMIT ---------------- */
  const handleSubmit = () => {
    dispatch(
      GetHistoryData({
        token: localStorage.getItem("token"),
        uniqueProjectId,
        startDate,
        endDate,
      })
    ).then((res) => {
      if (res?.payload?.status === 200) {
        const response = res.payload.responseBody.data;
        // Normal history always exists
        arrangeData(response?.currentRange?.data || response);

        // Comparison only when available
        if (response.currentRange && response.previousRange) {


          arrangeComparisonData(response);
          setComparisonHisData(response);
        }

        setHisData(response?.currentRange?.data);
      }
    });
  };

  /** ---------------- STATS ---------------- */
  const calculateStatistics = (data) => {
    const min = [];
    const max = [];
    const avg = [];
    const sum = [];
    const sub = [];

    const cols = data[0]?.length || 0;

    for (let i = 0; i < cols; i++) {
      min[i] = i === 0 ? "Min" : Infinity;
      max[i] = i === 0 ? "Max" : -Infinity;
      avg[i] = i === 0 ? "Avg" : 0;
      sum[i] = i === 0 ? "Sum" : 0;
      sub[i] = i === 0 ? "Sub" : 0;
    }

    data.forEach((row) => {
      row.forEach((v, i) => {
        if (i === 0 || isNaN(v)) return;
        min[i] = Math.min(min[i], v);
        max[i] = Math.max(max[i], v);
        sum[i] += Number(v);
      });
    });

    for (let i = 1; i < cols; i++) {
      avg[i] = (sum[i] / data.length).toFixed(2);
    }

    if (data.length > 1) {
      for (let i = 1; i < cols; i++) {
        sub[i] = data[data.length - 1][i] - data[0][i];
      }
    }

    return { minValues: min, maxValues: max, avgValues: avg, sumValues: sum, subValues: sub };
  };

  const handleDownloadCSV = () => {

    if (oneDayDataTs.length > 0) {

      const csvHeaders = ["Timestamp", ...channelNames];

      const { minValues, maxValues, avgValues, sumValues, subValues } = calculateStatistics(oneDayDataTs);

      const csvData = [
        csvHeaders, // Headers
        ...oneDayDataTs, // All rows of data
      ];

      if (selectedFunctions.includes("Min")) {
        csvData.push(minValues);
      }

      if (selectedFunctions.includes("Max")) {
        csvData.push(maxValues);
      }

      if (selectedFunctions.includes("Avg")) {
        csvData.push(avgValues);
      }

      if (selectedFunctions.includes("Sum")) {
        csvData.push(sumValues);
      }

      if (selectedFunctions.includes("Sub")) {
        csvData.push(subValues);
      }

      const csvContent = csvData.map((row) => row.join(",")).join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = "data.csv";
      link.click();
    }
  };

  /** ---------------- RENDER ---------------- */
  return (
    <>
      <div className={styles.page}>
        <div className={styles.tabWrapper}>
          {["Graph", "Table", "ComparisonGraph"].map((t) => (
            <label
              key={t}
              className={tab === t ? `${styles.tabLabel} ${styles.active}` : styles.tabLabel}
              onClick={() => setTab(t)}
            >
              {t}
            </label>
          ))}
        </div>

        <div className={styles.body}>
          {tab === "Graph" && (
            <>
              <div className={styles.graphContainer} ref={graphRef}>

                {graphType === "Bar" ? (
                  <BarHistory current={currentChart} />
                ) : graphType === "Histogram" ? (

                  <HistogramHistory viewData={chartDataDay} xInterval={isExporting ? "" : xInterval} graphType={graphType} />
                ) : graphType === "Doughnut" ? (

                  <DoughnutHistory current={currentChart} />
                ) : (

                  <LineHistory viewData={chartDataDay} xInterval={isExporting ? "" : xInterval} graphType={graphType} historical />
                )}

              </div>
              <button className={styles.downloadBtn} onClick={() => setIsGraphLabels(true)}>
                Download Graph
              </button>
            </>
          )}


          {tab === "ComparisonGraph" && (
            <ComparisonGraph current={currentChart} previous={previousChart} xInterval={xInterval} graphType={graphType} />
          )}

          {tab === "Table" && (
            <>
              <div className={styles.tableContainer}>
                <table>
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      {channelNames?.map((h, i) => <th key={i}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {oneDayDataTs?.map((row, i) => (
                      <tr key={i}>
                        {row?.map((v, j) => <td key={j}>
                          {v instanceof Date ? v.toLocaleString() : v}
                        </td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className={styles.tableDownWrapper}>
                <button className={styles.tableDownBtn} onClick={handleDownloadCSV}>
                  Download CSV
                </button>
                <button className={styles.tableDownBtn} onClick={() => setIsPDFModel(true)}>
                  Download PDF
                </button>

              </div>
            </>

          )}
        </div>

        <div className={styles.footer}>
          <input type="datetime-local" onChange={e => setStartDate(+new Date(e.target.value))} />
          <label>to</label>
          <input type="datetime-local" onChange={e => setEndDate(+new Date(e.target.value))} />
          <button className={styles.btn} onClick={handleSubmit}>Submit</button>
          <button className={styles.btn} onClick={() => setIsTagGroupsModel(true)}>Select Tags</button>
          <button className={styles.btn} onClick={() => setIsFunctionModel(true)}>Add Functions</button>
          <button className={styles.btn} onClick={() => { setIsXInterval(true); setXInterval(xInterval) }}>Graph Interval</button>
          <select onChange={(e) => setGraphType(e.target.value)} className={styles.selectGraph}>
            <option value="Line">Line</option>
            <option value="Fill">Fill</option>
            <option value="Bar">Bar</option>
            <option value="Doughnut">Doughnut</option>
            <option value="Histogram">Histogram</option>
          </select>
        </div>
      </div>

      {isGraphLabels && (
        <GraphLabelsModel
          graphRef={graphRef}
          setIsGraphLabels={setIsGraphLabels}
          oneDayDataTs={oneDayDataTs}
          selectedFunctions={selectedFunctions}
          channelNames={channelNames}
          calculateStatistics={calculateStatistics}
          setIsExporting={setIsExporting}
        />
      )}

      {isTagGroupsModel && (
        <TagSelectionModel
          channelNames={channelNames}
          setSelectedChannels={setSelectedChannels}
          selectedChannels={selectedChannels}
          setSelectedGroups={setSelectedGroups}
          selectedGroups={selectedGroups}
          setIsTagGroupsModel={setIsTagGroupsModel}
          arrangeData={arrangeData}
          hisData={hisData}
          allChannels={allChannels}
          comparisonHisData={comparisonHisData}
          arrangeComparisonData={arrangeComparisonData}
        />
      )}

      {isFunctionModel && (
        <FunctionModel
          setIsFunctionModel={setIsFunctionModel}
          setSelectedFunctions={setSelectedFunctions}
        />
      )}

      {isPDFModel && (
        <ModelPDFDetails
          data={oneDayDataTs}
          header={["Timestamp", ...channelNames]}
          setOpenModel={setIsPDFModel}
          calculateStatistics={calculateStatistics}
          selectedFunctions={selectedFunctions}
        />
      )}

      {isXInterval && (
        <GraphInterval
          setIsXInterval={setIsXInterval}
          setXInterval={setXInterval}
          xInterval={xInterval}
        />
      )}


    </>
  );
};

export default History;
